import * as anchor from "@coral-xyz/anchor";
import chalk from "chalk";
import { Keypair, PublicKey, TransactionInstruction } from "@solana/web3.js";
import {
  getNetworkConstants,
  NetworkConstants,
  createMultisigService,
  MultisigService,
} from "@crunchdao/sdk";
import { getNetworkFromUrl, setLevel, log } from "@crunchdao/sdk";
import {
  Coordinator,
  Staking,
  CoordinatorIDL,
  StakingIDL,
  CoordinatorPool,
  CoordinatorPoolIDL,
} from "@crunchdao/programs";
import { ConfigManager } from "../config";
import { createAnchorProvider } from "../providers";
import { formatAddressWithLink, formatTransactionWithLink } from "../utils/address-utils";
import { createTable, createTableWithoutBorders } from "../utils/table-utils";

export abstract class BaseCommand {
  protected provider: anchor.AnchorProvider;
  protected program: anchor.Program<Coordinator>;
  protected coordinatorPoolProgram: anchor.Program<CoordinatorPool>;
  protected stakingProgram: anchor.Program<Staking>;
  protected resolvedOptions: {
    network: string;
    wallet: string;
    loglevel: string;
    output: string | null;
    quiet: boolean;
  };
  protected networkConstants: NetworkConstants;

  protected cruncherFeePayerKeypair?: Keypair;
  protected cruncherFeePayerPublicKey?: PublicKey;
  protected multisigService: MultisigService | null = null;
  protected network: string;

  constructor(options: {
    url?: string;
    wallet?: string;
    loglevel?: string;
    output?: string;
    multisig?: string;
    quiet?: boolean;
  }) {
    const configManager = new ConfigManager();
    const globalConfig = configManager.load();

    // Prioritize command line options > config values > defaults
    const networkOrUrl = options.url || globalConfig.url || "mainnet-beta";
    const wallet = options.wallet || globalConfig.wallet;
    const loglevel = options.loglevel || globalConfig.loglevel;
    const output = options.output || globalConfig.output || null;
    const multisigAddress = options.multisig || globalConfig.multisigAddress;
    const quiet = options.quiet || false;

    // Create a config object that includes the resolved format option
    setLevel(loglevel as any);

    // Determine the actual network name for explorer links and other features
    this.network = getNetworkFromUrl(networkOrUrl);

    this.networkConstants = getNetworkConstants(this.network);

    // Store resolved options for use in error handlers
    this.resolvedOptions = { network: networkOrUrl, wallet, loglevel, output, quiet };

    this.provider = createAnchorProvider(networkOrUrl, wallet);
    anchor.setProvider(this.provider);

    // Log RPC endpoint for transparency
    //log.info(`🔗 RPC Endpoint: ${this.provider.connection.rpcEndpoint}`);

    // Create program using imported IDL (will be inlined by tsup)
    this.program = new anchor.Program<Coordinator>(CoordinatorIDL, this.provider);
    this.stakingProgram = new anchor.Program<Staking>(StakingIDL, this.provider);
    this.coordinatorPoolProgram = new anchor.Program<CoordinatorPool>(
      CoordinatorPoolIDL,
      this.provider
    );

    // Initialize multisig service if multisig address is provided
    if (multisigAddress) {
      try {
        this.multisigService = createMultisigService(
          this.provider.connection,
          multisigAddress,
          this.provider.wallet
        );
      } catch (error) {
        console.log(chalk.red(`⚠️  Failed to initialize multisig service: ${error}`));
      }
    }
  }

  /**
   * Handle errors by logging them and exiting with the specified exit code
   * @param error The error to handle
   * @param exitCode The exit code to use (default: 1)
   */
  protected handleError(error: any, exitCode: number = 1): never {
    log.error("Error:", error);
    process.exit(exitCode);
  }

  protected output(data: any) {
    console.log(JSON.stringify(data, null, 2));
  }

  /**
   * Display a highlighted action banner to make the operation clear
   * @param action The action being performed (e.g., "Updating claim expiry", "Approving coordinator")
   * @param details Additional details about the action (e.g., "to 30 days", "GPXg...xyz")
   */
  protected displayAction(action: string, details?: string): void {
    const separator = "═".repeat(60);

    console.log(`\n${chalk.blue(separator)}`);

    if (details) {
      console.log(`${chalk.yellow.bold(action)} ${chalk.white(details)}`);
    } else {
      console.log(`${chalk.yellow.bold(action)}`);
    }

    console.log(`${chalk.blue(separator)}\n`);
  }

  /**
   * Display action completion with success/proposal status
   * @param action The completed action
   * @param isMultisig Whether this was a multisig proposal
   */
  protected displayActionComplete(action: string, isMultisig: boolean = false): void {
    if (isMultisig) {
      console.log(
        `\n${chalk.green.bold("✅")} ${chalk.white(action)} ${chalk.cyan("proposal created")}`
      );
    } else {
      console.log(
        `\n${chalk.green.bold("✅")} ${chalk.white(action)} ${chalk.green(
          "completed successfully"
        )}`
      );
    }
  }

  /**
   * Execute instructions either directly or propose to multisig
   */
  protected async executeOrProposeInstructions(
    instructionFunction: () => Promise<TransactionInstruction[]>,
    memo: string = "Transaction via CLI",
    options: {
      useSeparateTransactions?: boolean;
      useMultisigBatch?: boolean;
    } = {}
  ): Promise<void> {
    if (this.multisigService) {
      if (options.useMultisigBatch) {
        await this.proposeBatchToMultisig(instructionFunction, memo);
      } else {
        // Multisig mode - propose instructions to multisig
        await this.proposeInstructionsToMultisig(instructionFunction, memo);
      }
    } else {
      // Direct execution mode - convert to transaction and execute
      const instructions = await instructionFunction();

      if (options.useSeparateTransactions) {
        // Execute each instruction in a separate transaction
        const signatures: string[] = [];

        for (let i = 0; i < instructions.length; i++) {
          const tx = new anchor.web3.Transaction().add(instructions[i]);
          const signature = await this.provider.sendAndConfirm(tx);
          signatures.push(signature);

          // Display each transaction with clickable link
          const formattedTx = formatTransactionWithLink(signature, this.network);
          console.log(
            `${chalk.green.bold(
              `✅ Transaction ${i + 1}/${instructions.length} executed:`
            )} ${formattedTx}`
          );
        }

        console.log(`${chalk.green.bold("✅ All transactions completed successfully!")}`);
      } else {
        // Execute all instructions in a single transaction
        const tx = new anchor.web3.Transaction().add(...instructions);
        const signature = await this.provider.sendAndConfirm(tx);

        // Display transaction with clickable link to Solana Explorer
        const formattedTx = formatTransactionWithLink(signature, this.network);
        console.log(`${chalk.green.bold("✅ Transaction executed:")} ${formattedTx}`);
      }
    }
  }

  /**
   * Propose instructions to the multisig
   */
  private async proposeInstructionsToMultisig(
    instructionFunction: () => Promise<TransactionInstruction[]>,
    memo: string
  ): Promise<void> {
    try {
      console.log(`${chalk.yellow.bold("Preparing multisig proposal")}`);

      // Get multisig info first
      const multisigInfo = await this.multisigService!.getMultisigInfo();

      // Create multisig details table
      console.log(`\n${chalk.blue.bold("Multisig Details")}`);
      const detailsTable = createTableWithoutBorders();

      detailsTable.push(
        [chalk.yellow("Address:"), formatAddressWithLink(multisigInfo.address, this.network)],
        [
          chalk.yellow("Vault:"),
          formatAddressWithLink(this.multisigService!.getVaultPDA().toBase58(), this.network),
        ],
        [
          chalk.yellow("Proposer:"),
          formatAddressWithLink(this.provider.wallet.publicKey.toBase58(), this.network),
        ],
        [chalk.yellow("Description:"), chalk.cyan(memo)],
        [
          chalk.yellow("Threshold:"),
          chalk.white(`${multisigInfo.threshold}/${multisigInfo.members} signatures required`),
        ],
        [chalk.yellow("Next Transaction:"), chalk.white(`#${multisigInfo.transactionIndex + 1}`)]
      );

      console.log(detailsTable.toString());

      // Get the transaction instructions
      const instructions = await instructionFunction();

      console.log(
        `\n${chalk.magenta("Proposing")} ${chalk.white.bold(instructions.length)} ${chalk.magenta(
          "instruction(s)..."
        )}`
      );

      // Actually propose the transaction to the multisig
      const proposalResult = await this.multisigService!.proposeTransaction(instructions, memo);

      // Success message with proposal details
      console.log(`\n${chalk.green.bold("✅ Proposal created successfully!")}`);

      // Create proposal results table
      const proposalTable = createTableWithoutBorders();

      proposalTable.push(
        [chalk.cyan.bold("Proposal ID"), chalk.white.bold(`#${proposalResult.transactionIndex}`)],
        [chalk.cyan.bold("Review URL"), chalk.blue.underline(proposalResult.proposalUrl)],
        [chalk.cyan.bold("Proposal Signature"), chalk.gray(proposalResult.signature)],
        [chalk.cyan.bold("Status"), chalk.yellow("Awaiting Approvals")],
        [
          chalk.cyan.bold("Required"),
          chalk.white(`${multisigInfo.threshold} of ${multisigInfo.members} signatures`),
        ]
      );

      console.log(`\n${proposalTable.toString()}`);

      console.log(
        `\n${chalk.blue("💡 Next:")} ${chalk.white(
          "Ask other multisig members to approve this proposal"
        )}`
      );
      console.log(
        `${chalk.yellow("⚠️  Note:")} ${chalk.gray(
          "Transaction executes automatically after"
        )} ${chalk.white.bold(multisigInfo.threshold)} ${chalk.gray("approvals")}`
      );
    } catch (error) {
      throw new Error(`Failed to propose to multisig: ${error}`);
    }
  }

  /**
   * Propose batch to the multisig
   */
  private async proposeBatchToMultisig(
    instructionSetsFunction: () => Promise<TransactionInstruction[]>,
    memo: string
  ): Promise<void> {
    try {
      console.log(`${chalk.yellow.bold("Preparing multisig batch proposal")}`);

      // Get multisig info first
      const multisigInfo = await this.multisigService!.getMultisigInfo();

      // Create multisig details table
      console.log(`\n${chalk.blue.bold("Multisig Details")}`);
      const detailsTable = createTableWithoutBorders();

      detailsTable.push(
        [chalk.yellow("Address:"), formatAddressWithLink(multisigInfo.address, this.network)],
        [
          chalk.yellow("Vault:"),
          formatAddressWithLink(this.multisigService!.getVaultPDA().toBase58(), this.network),
        ],
        [
          chalk.yellow("Proposer:"),
          formatAddressWithLink(this.provider.wallet.publicKey.toBase58(), this.network),
        ],
        [
          chalk.yellow("Threshold:"),
          chalk.white(`${multisigInfo.threshold}/${multisigInfo.members} signatures required`),
        ],
        [chalk.yellow("Next Batch:"), chalk.white(`#${multisigInfo.transactionIndex + 1}`)]
      );

      console.log(detailsTable.toString());

      // Get the instruction sets
      const instructionSets = await instructionSetsFunction();

      console.log(
        `\n${chalk.magenta("Creating batch with")} ${chalk.white.bold(
          instructionSets.length
        )} ${chalk.magenta("instruction(s)...")}`
      );

      // Actually propose the batch to the multisig
      const batchResult = await this.multisigService!.proposeBatch(instructionSets, memo);

      // Success message with batch details
      console.log(`\n${chalk.green.bold("✅ Batch proposal created successfully!")}`);

      // Create batch results table
      const batchTable = createTable();

      batchTable.push(
        [chalk.cyan.bold("Batch ID"), chalk.white.bold(`#${batchResult.batchIndex}`)],
        [chalk.cyan.bold("Transactions"), chalk.white.bold(`${batchResult.transactionsAdded}`)],
        [chalk.cyan.bold("Review URL"), chalk.blue.underline(batchResult.proposalUrl)],
        [chalk.cyan.bold("Proposal Signature"), chalk.gray(batchResult.signature)],
        [chalk.yellow("Description:"), chalk.cyan(memo)],
        [chalk.cyan.bold("Status"), chalk.yellow("Awaiting Approvals")],
        [
          chalk.cyan.bold("Required"),
          chalk.white(`${multisigInfo.threshold} of ${multisigInfo.members} signatures`),
        ]
      );

      console.log(`\n${batchTable.toString()}`);

      console.log(
        `\n${chalk.blue("💡 Next:")} ${chalk.white(
          "Ask other multisig members to approve this batch proposal"
        )}`
      );
      console.log(
        `${chalk.yellow("⚠️  Note:")} ${chalk.gray(
          "All transactions execute atomically after"
        )} ${chalk.white.bold(multisigInfo.threshold)} ${chalk.gray("approvals")}`
      );
    } catch (error) {
      throw new Error(`Failed to propose batch to multisig: ${error}`);
    }
  }

  /**
   * Check if multisig mode is enabled
   */
  protected isMultisigMode(): boolean {
    return this.multisigService !== null;
  }

  public getSigner(): PublicKey {
    return this.isMultisigMode()
      ? this.multisigService!.getVaultPDA()
      : this.provider.wallet.publicKey;
  }
}
